import { H1, H3 } from '@/components'
import {
  Card,
  Checkbox,
  MonthPicker,
  UserSelect,
} from '@/components_deprecated'
import { LeaderGroupSelect } from '@/components_deprecated/Select/LeaderGroupSelect'
import { Field } from '@/modules/companies'
import { WORK_PERIODS_START_STOP_COUNTRY } from '@/modules/salary/queries'
import { format } from '@/utils'
import { getNodes } from '@/utils/graphql'
import { usePermissions, useUser, useLeaderGroups } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  endOfMonth,
  formatISO,
  isSameDay,
  parseISO,
  startOfMonth,
} from 'date-fns'
import React from 'react'
import { isMobileOnly } from 'react-device-detect'
import styled, { useTheme } from 'styled-components'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
  position: relative;
`

const InfoContentWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`

const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;

  margin-bottom: 1rem;
  padding: 0;

  section {
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    gap: 1rem;
    padding: 1rem;

    & + section {
      grid-template-columns: auto;
      border-left: 1px solid ${props => props.theme.colors.gray8};
      align-items: center;
    }
  }

  ${props => props.theme.media.mobile} {
    grid-template-columns: 1fr;

    section {
      grid-template-columns: 1fr;
      padding: 1rem;

      & + section {
        padding-top: 0;
        border-left: 0;
      }
    }
  }
`

const WorkPeriodCard = styled(Card)<{ $ok: boolean }>`
  background-color: ${props =>
    props.$ok ? props.theme.colors.green100 : props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.gray8};
`

export const StartStopCountryRegistrations: React.FC = () => {
  const translations = useTranslate({
    salaryCode: 'common.salary-code',
    amount: 'common.amount',
    activities: 'common.activities',
    workDay: 'common.work-day',
    dayRest: 'common.day-rest',
    weekRest: 'common.week-rest',
    showRest: 'common.show-rest',
    export: 'common.export',
    period: 'common.period',
    weekN: ['common.week-n', { n: 0 }],
    workOverview: 'salary.work-overview',
    manualRegistrations: 'activities.manual-registrations',

    nUsersNeedApproval: ['salary.n-users-need-approval', { n: 0 }],
    oneUserNeedsApproval: 'salary.one-user-needs-approval',
    nWorkPeriods: ['salary.n-work-periods', { n: 0 }],

    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'common.overtime',
    available: 'activities.availability-time',
    lockedValue: 'salary.locked-value',
    lock: 'salary.lock',

    toast: {
      mailSuccess: 'exports.success-email-process',
    },
    error: {
      generic: 'error.generic-server-error',
    },

    salaryMode: 'common.salary-mode',
    month: 'common.month',
    user: 'common.user',
    chooseGroup: 'common.choose-group',

    overtimeThatOverlapsMonths: 'salary.overtime-that-overlaps-months',
    nextMonth: 'common.next-month',
    workPeriods: 'salary.work-periods',
    confirmHours: 'salary.confirm-hours',

    showResolved: 'salary.show-resolved',
    showOk: 'activities.show-ok',

    leaderGroups: 'companies.leader-groups',
    noManualRegistration: 'salary.no-manual-registration',
  })

  const me = useUser()
  const theme = useTheme()
  const { hasPermissions } = usePermissions()
  const canSeeOthers = hasPermissions(
    PERMISSIONS.workPeriod.view.othersworkperiod,
    true
  )

  const { hasAccess, myLeaderGroups, groupsLoading } = useLeaderGroups()

  const form = useForm<{
    user: string | null
    datetimeStart: Date
    datetimeEnd: Date
    showResolved: boolean
    leaderGroup: string | null
    leaderGroupsUsers: string[]
  }>({
    values: {
      user: null,
      datetimeStart: startOfMonth(new Date()),
      datetimeEnd: endOfMonth(new Date()),
      showResolved: false,
      leaderGroup: null,
      leaderGroupsUsers: [],
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'startStopCountryRegistrations',
        storeFormStateKey: 'startStopCountryRegistrations',
        transformValues: {
          user: val => {
            return val && canSeeOthers ? String(val) : null
          },
          datetimeStart: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
          datetimeEnd: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
        },
      },
    },
  })

  const { data } = useQuery(WORK_PERIODS_START_STOP_COUNTRY, {
    variables: {
      user: form.formValues.user ?? (canSeeOthers ? null : me.id),
      datetimeStart: formatISO(form.formValues.datetimeStart),
      datetimeEnd: formatISO(form.formValues.datetimeEnd),
    },
  })

  const workPeriods = getNodes(data?.allWorkPeriods)

  const displayWorkPeriods = workPeriods.filter(wp => {
    if (
      !!wp.startCountry &&
      !!wp.stopCountry &&
      !form.formValues.showResolved
    ) {
      return false
    }
    const user = form.formValues.user
    if (user && form.formValues.leaderGroup) {
      return (
        form.formValues.leaderGroupsUsers.includes(user) &&
        form.formValues.leaderGroupsUsers.includes(wp.user.id)
      )
    }
    if (user || !form.formValues.leaderGroup) return true
    return form.formValues.leaderGroupsUsers.includes(wp.user.id)
  })

  const changeLeaderGroup = (value: string | null) => {
    const selectedGroup = myLeaderGroups.find(g => g.id === value)
    form.updateForm({
      leaderGroup: value,
      leaderGroupsUsers: selectedGroup?.users.map(user => user.id) ?? [],
    })
  }

  return (
    <Wrapper>
      <InfoContentWrapper>
        <H1>{translations.manualRegistrations} </H1>
      </InfoContentWrapper>
      <Controls>
        <section>
          {canSeeOthers && (
            <Field>
              <label>{translations.user}</label>
              <UserSelect
                value={form.formValues.user}
                grayed
                width="100%"
                minWidth={isMobileOnly ? '0' : '260px'}
                height="3.875rem"
                onChange={user => form.updateForm({ user: user ?? null })}
                filterSalaryResponsible={false}
                filterLeaderGroup={true}
                nullable
              />
            </Field>
          )}
          {hasAccess && (
            <Field>
              <label>{translations.leaderGroups}</label>
              <LeaderGroupSelect
                value={form.formValues.leaderGroup}
                loading={groupsLoading}
                options={
                  myLeaderGroups?.map(group => ({
                    label: group.name,
                    value: group.id,
                  })) ?? []
                }
                fullWidth
                grayed={true}
                onChange={value => changeLeaderGroup(value)}
                placeholder={translations.chooseGroup}
                nullable
              />
            </Field>
          )}

          <Field>
            <label>{translations.showOk}</label>
            <Checkbox
              checked={form.formValues.showResolved}
              onChange={checked => form.updateForm({ showResolved: checked })}
              boxSize="3.875rem"
              padding="2rem"
              boxBorderRadius="8px"
            />
          </Field>
        </section>
        <section>
          <Field>
            <label>{translations.month}</label>
            <MonthPicker
              value={form.formValues.datetimeStart}
              onChange={date => {
                const start = startOfMonth(date)
                const end = endOfMonth(date)
                form.updateForm({
                  datetimeStart: start,
                  datetimeEnd: end,
                })
              }}
            />
          </Field>
        </section>
      </Controls>

      <div className="flex flex-col gap-2">
        {displayWorkPeriods.map(workPeriod => {
          const startDate = parseISO(workPeriod.datetimeStart)
          const endDate = parseISO(workPeriod.datetimeEnd)
          const isSameDayValue = isSameDay(startDate, endDate)
          return (
            <WorkPeriodCard
              key={workPeriod.id}
              $ok={
                (!!workPeriod.startCountry && !!workPeriod.stopCountry) ?? null
              }
            >
              <div>
                <H3>{workPeriod.user.fullName}</H3>
                <div>
                  <strong>{translations.period}: </strong>{' '}
                  {isSameDayValue
                    ? format(startDate, 'dd.MM.yyyy HH:mm')
                    : format(startDate, 'dd.MM.yyyy HH:mm')}{' '}
                  -{' '}
                  {isSameDayValue
                    ? format(endDate, 'HH:mm')
                    : format(endDate, 'dd.MM.yyyy HH:mm')}
                </div>
                <div className="flex gap-1">
                  <strong>{translations.manualRegistrations}: </strong>
                  <div
                    style={{
                      color: workPeriod.startCountry
                        ? theme.colors.green
                        : theme.colors.red,
                    }}
                    className={workPeriod.startCountry ? '' : 'italic'}
                  >
                    {workPeriod.startCountry
                      ? workPeriod.startCountry.country
                      : 'N/A'}
                  </div>
                  -
                  <div
                    style={{
                      color: workPeriod.stopCountry
                        ? theme.colors.green
                        : theme.colors.red,
                    }}
                    className={workPeriod.stopCountry ? '' : 'italic'}
                  >
                    {workPeriod.stopCountry
                      ? workPeriod.stopCountry.country
                      : 'N/A'}
                  </div>
                </div>
              </div>
            </WorkPeriodCard>
          )
        })}
      </div>
    </Wrapper>
  )
}
